import { useMutation } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type { ChatMessage } from '../types';

/**
 * Generative (LLM) interactions. Each calls a real backend endpoint. The
 * backend returns 200 with a null/empty payload when the provider is
 * unavailable; callers combine these with `useAICapability` to render honest
 * system states rather than fabricated output.
 */

export function useAIChat() {
  return useMutation({
    mutationFn: async ({ message, history }: { message: string; history: ChatMessage[] }) => {
      const { data } = await api.post<{ reply: string }>('/api/ai/chat', { message, history });
      return data.reply ?? '';
    },
  });
}

export function useFollowUpDraft() {
  return useMutation({
    mutationFn: async (input: { leadName?: string; niche?: string; notes?: string; disposition?: string }) => {
      const { data } = await api.post<{ draft: string | null }>('/api/ai/follow-up-draft', input);
      return data.draft;
    },
  });
}

export function useMeetingAgenda() {
  return useMutation({
    mutationFn: async (meetingId: string) => {
      const { data } = await api.post<{ agenda: string | null }>('/api/ai/meeting-agenda', { meetingId });
      return data.agenda;
    },
  });
}

export function useLeadInsights() {
  return useMutation({
    mutationFn: async (leadId: string) => {
      const { data } = await api.post<{ insight: string | null }>('/api/ai/lead-insights', { leadId });
      return data.insight;
    },
  });
}

export function useCallCoach() {
  return useMutation({
    mutationFn: async (input: { leadName?: string; disposition?: string; notes?: string; duration?: number }) => {
      const { data } = await api.post<{ tip: string | null }>('/api/ai/call-coach', input);
      return data.tip;
    },
  });
}

export interface ParseIntentResult {
  success: boolean;
  entityType: string | null;
  fields: Record<string, unknown>;
  missingFields: string[];
  message: string;
}

export function useParseIntent() {
  return useMutation({
    mutationFn: async (text: string) => {
      const { data } = await api.post<ParseIntentResult>('/api/ai/parse-intent', { text });
      return data;
    },
  });
}
